import isPlainObject from 'lodash/isPlainObject'

import { IFileConflictContext } from './copilot-conflict-context'

// ---------------------------------------------------------------------------
// Types & interfaces
// ---------------------------------------------------------------------------

/** Resolution suggestion for a single conflicted file. */
export interface IFileResolution {
  /** Repository-relative file path that was resolved. */
  readonly path: string
  /** The fully resolved file content (all conflict markers removed). */
  readonly resolvedContent: string
  /** Human-readable explanation of how and why conflicts were resolved this way. */
  readonly reasoning: string
}

/** Complete response from Copilot conflict resolution. */
export interface ICopilotConflictResolutionResponse {
  /** Resolution suggestions, one per conflicted file. */
  readonly resolutions: ReadonlyArray<IFileResolution>
}

/** Progress information emitted during conflict resolution. */
export interface IConflictResolutionProgress {
  readonly filesResolved: number
  readonly filesTotal: number
}

// ---------------------------------------------------------------------------
// Error class
// ---------------------------------------------------------------------------

/**
 * Error subclass for parse and validation failures from Copilot responses.
 * Used to distinguish retryable errors (bad LLM output) from transport
 * errors (timeouts, auth, session creation) which should fail fast.
 */
export class CopilotValidationError extends Error {
  public constructor(message: string) {
    super(message)
    this.name = 'CopilotValidationError'
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Maximum number of files to resolve in a single prompt. When the total
 * exceeds this threshold, the engine batches files into parallel chunks.
 */
export const SinglePromptFileLimit = 20

/** Maximum number of chunks to resolve concurrently. */
export const MaxConcurrentChunks = 5

/**
 * System prompt for the Copilot conflict resolution session.
 */
export const ConflictResolutionSystemPrompt = `
You have all the context you need below. Do NOT attempt to use tools. Respond ONLY with the JSON format specified.

You are an expert Git conflict resolver. Your task is to analyze conflicts from merge, rebase, or cherry-pick operations and produce correct, clean resolutions.

You will receive:
- Labels for both sides of the conflict (e.g., branch names or commit references)
- The conflict markers from each conflicted file (ours, theirs, and optionally base content)
- Context lines surrounding each conflict
- When available: recent commit messages from both sides explaining the intent behind changes
- When available: the pull request title and description providing higher-level context

Your job:
1. Understand the INTENT behind each side's changes using commit messages and PR context when available
2. Resolve each conflict by producing the correct merged content
3. Explain your reasoning for each resolution

Resolution guidelines:
- Make the MINIMAL changes necessary to resolve the conflict — do not refactor, reformat, or alter code outside the conflicted regions
- When both sides add complementary code (e.g., different imports, different functions), combine them
- When both sides modify the same code differently, use commit messages and PR context to determine the correct resolution
- When one side deletes code the other modifies, determine if the deletion was intentional
- Preserve code correctness: imports, types, formatting must be valid
- When in doubt, prefer the approach that maintains backward compatibility

You MUST respond with valid JSON in this exact format:
{
  "resolutions": [
    {
      "path": "relative/file/path.ts",
      "resolvedContent": "the complete resolved file content with all conflicts resolved",
      "reasoning": "explanation of how you resolved each conflict and why"
    }
  ]
}

Important:
- resolvedContent must contain the COMPLETE file content (not just the conflicted sections)
- All conflict markers must be removed in the resolved content
- Include one resolution entry per conflicted file
`

// ---------------------------------------------------------------------------
// Functions
// ---------------------------------------------------------------------------

/**
 * Normalize a file path returned by the LLM. The model may return
 * Windows-style backslashes (`src\\file.ts`), a leading `./`, or redundant
 * separators — all of which would cause validation to reject an otherwise
 * correct resolution.
 */
function normalizeLLMPath(raw: string): string {
  return raw
    .trim()
    .replace(/\\/g, '/')
    .replace(/^\.\//, '')
    .replace(/\/\/+/g, '/')
}

/**
 * Parse the raw string response from the Copilot SDK into a structured
 * conflict resolution response.
 *
 * Handles markdown code-block wrapping (` ```json ... ``` `) and validates
 * all required fields.
 */
export function parseCopilotConflictResolution(
  content: string
): ICopilotConflictResolutionResponse {
  // Build a list of JSON candidates from the response, trying different
  // extraction strategies. Non-greedy handles the common single-block and
  // multi-block cases. Greedy handles triple backticks embedded inside JSON
  // content. Raw content handles responses with no fences at all.
  const nonGreedy =
    content.match(/```json\s*([\s\S]*?)```/) ||
    content.match(/```\s*([\s\S]*?)```/)
  const greedy =
    content.match(/```json\s*([\s\S]*)```/) ||
    content.match(/```\s*([\s\S]*)```/)

  const candidates: Array<string> = []
  if (nonGreedy) {
    candidates.push(nonGreedy[1].trim())
  }
  if (greedy && greedy[1].trim() !== nonGreedy?.[1]?.trim()) {
    candidates.push(greedy[1].trim())
  }
  candidates.push(content.trim())

  let parsed: unknown
  let parseError: Error | undefined
  for (const candidate of candidates) {
    try {
      parsed = JSON.parse(candidate)
      parseError = undefined
      break
    } catch {
      parseError = new CopilotValidationError(
        'Copilot returned invalid JSON for conflict resolution generation'
      )
    }
  }
  if (parseError) {
    throw parseError
  }

  if (!isPlainObject(parsed)) {
    throw new CopilotValidationError(
      'Copilot returned an invalid conflict resolution payload: expected an object'
    )
  }

  const obj = parsed as Record<string, unknown>
  const { resolutions } = obj

  if (!Array.isArray(resolutions)) {
    throw new CopilotValidationError(
      'Copilot returned an invalid conflict resolution payload: "resolutions" must be an array'
    )
  }

  if (resolutions.length === 0) {
    throw new CopilotValidationError(
      'Copilot returned an invalid conflict resolution payload: "resolutions" must not be empty'
    )
  }

  const validated: Array<IFileResolution> = []

  for (let i = 0; i < resolutions.length; i++) {
    const entry: unknown = resolutions[i]

    if (!isPlainObject(entry)) {
      throw new CopilotValidationError(
        `Copilot returned an invalid conflict resolution payload: resolution at index ${i} must be an object`
      )
    }

    const obj = entry as Record<string, unknown>
    const { path, resolvedContent, reasoning } = obj

    if (typeof path !== 'string' || path.trim().length === 0) {
      throw new CopilotValidationError(
        `Copilot returned an invalid conflict resolution payload: "path" at index ${i} must be a non-empty string`
      )
    }

    if (typeof resolvedContent !== 'string') {
      throw new CopilotValidationError(
        `Copilot returned an invalid conflict resolution payload: "resolvedContent" at index ${i} must be a string`
      )
    }

    if (/^<{7}\s/m.test(resolvedContent) && /^={7}$/m.test(resolvedContent)) {
      throw new CopilotValidationError(
        `Copilot returned an invalid conflict resolution payload: "resolvedContent" at index ${i} still contains conflict markers`
      )
    }

    if (typeof reasoning !== 'string' || reasoning.trim().length === 0) {
      throw new CopilotValidationError(
        `Copilot returned an invalid conflict resolution payload: "reasoning" at index ${i} must be a non-empty string`
      )
    }

    validated.push({ path: normalizeLLMPath(path), resolvedContent, reasoning })
  }

  return { resolutions: validated }
}

/**
 * Validate that a parsed resolution response matches the expected set of
 * file paths. Throws CopilotValidationError on unexpected paths, duplicates,
 * or missing files.
 */
export function validateResolutionPaths(
  resolutions: ReadonlyArray<IFileResolution>,
  expectedPaths: ReadonlySet<string>
): void {
  const returnedPaths = new Set(resolutions.map(r => r.path))

  for (const path of returnedPaths) {
    if (!expectedPaths.has(path)) {
      throw new CopilotValidationError(
        `Copilot returned resolution for unexpected file: ${path}`
      )
    }
  }

  if (returnedPaths.size !== resolutions.length) {
    throw new CopilotValidationError(
      'Copilot returned duplicate file paths in resolutions'
    )
  }

  const missingPaths: Array<string> = []
  for (const path of expectedPaths) {
    if (!returnedPaths.has(path)) {
      missingPaths.push(path)
    }
  }
  if (missingPaths.length > 0) {
    throw new CopilotValidationError(
      `Copilot did not return resolutions for: ${missingPaths.join(', ')}`
    )
  }
}

/**
 * Extract exported and imported symbols from conflict hunk content for
 * dependency detection. Scans all hunk sections (ours, theirs, context)
 * to find import paths, exported names, and referenced identifiers.
 */
export function extractSymbols(file: IFileConflictContext): {
  readonly exports: ReadonlySet<string>
  readonly importPaths: ReadonlySet<string>
  readonly references: ReadonlySet<string>
} {
  const exports = new Set<string>()
  const importPaths = new Set<string>()
  const references = new Set<string>()

  const textParts: Array<string> = []
  for (const hunk of file.hunks) {
    textParts.push(
      hunk.oursContent,
      hunk.theirsContent,
      hunk.contextBefore,
      hunk.contextAfter
    )
    if (hunk.baseContent !== null) {
      textParts.push(hunk.baseContent)
    }
  }
  const content = textParts.join('\n')

  for (const m of content.matchAll(
    /export\s+(?:function|const|let|class|interface|type|enum)\s+(\w+)/g
  )) {
    exports.add(m[1])
  }

  // Match all common import forms:
  //   import { a, b } from 'x'
  //   import X from 'x'
  //   import * as X from 'x'
  //   import X, { a, b } from 'x'
  //   import type { a } from 'x'
  for (const m of content.matchAll(
    /import\s+(?:type\s+)?(?:(\*\s+as\s+\w+)|(\w+)\s*,\s*\{([^}]+)\}|\{([^}]+)\}|(\w+))\s+from\s+['"]([^'"]+)['"]/g
  )) {
    // m[6] is always the import path
    importPaths.add(m[6])

    // Collect referenced names from whichever capture group matched
    const parts: Array<string> = []
    if (m[1]) {
      // import * as X — extract X
      const asName = m[1].replace(/^\*\s+as\s+/, '').trim()
      if (asName) {
        parts.push(asName)
      }
    } else if (m[2] && m[3]) {
      // import Default, { named } — both
      parts.push(m[2])
      parts.push(...m[3].split(','))
    } else if (m[4]) {
      // import { named }
      parts.push(...m[4].split(','))
    } else if (m[5]) {
      // import Default
      parts.push(m[5])
    }

    for (const name of parts) {
      const trimmed = name
        .trim()
        .replace(/^type\s+/, '')
        .split(/\s+as\s+/)[0]
        .trim()
      if (trimmed) {
        references.add(trimmed)
      }
    }
  }

  for (const m of content.matchAll(
    /(?:extends|implements|instanceof|new|typeof)\s+(\w+)/g
  )) {
    references.add(m[1])
  }

  return { exports, importPaths, references }
}

/**
 * Group files that share dependencies into clusters using Union-Find,
 * then pack clusters into chunks of `targetSize`. Files that import from
 * each other or reference each other's exports stay in the same chunk
 * so the model can reason about cross-file coherence.
 */
export function createDependencyAwareChunks(
  files: ReadonlyArray<IFileConflictContext>,
  targetSize: number
): ReadonlyArray<ReadonlyArray<IFileConflictContext>> {
  if (files.length <= targetSize) {
    return [Array.from(files)]
  }

  const fileSymbols = files.map(f => ({
    ...extractSymbols(f),
    baseName: f.path.replace(/\.[^.]+$/, '').replace(/^.*\//, ''),
  }))

  // Union-Find
  const parent = new Array<number>(files.length)
  for (let i = 0; i < files.length; i++) {
    parent[i] = i
  }

  function find(x: number): number {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]]
      x = parent[x]
    }
    return x
  }

  function union(a: number, b: number): void {
    const pa = find(a)
    const pb = find(b)
    if (pa !== pb) {
      parent[pa] = pb
    }
  }

  for (let i = 0; i < fileSymbols.length; i++) {
    for (let j = i + 1; j < fileSymbols.length; j++) {
      const a = fileSymbols[i]
      const b = fileSymbols[j]

      // Match import paths by path-segment boundary — not bare substring —
      // to avoid false positives with short basenames like "e" or "api".
      // Strip extension and directory from import path to get its base name.
      const aImportsB = [...a.importPaths].some(
        p => p.replace(/\.[^./]+$/, '').replace(/^.*\//, '') === b.baseName
      )
      const bImportsA = [...b.importPaths].some(
        p => p.replace(/\.[^./]+$/, '').replace(/^.*\//, '') === a.baseName
      )

      let sharedSymbols = false
      if (!sharedSymbols) {
        for (const exp of a.exports) {
          if (b.references.has(exp)) {
            sharedSymbols = true
            break
          }
        }
      }
      if (!sharedSymbols) {
        for (const exp of b.exports) {
          if (a.references.has(exp)) {
            sharedSymbols = true
            break
          }
        }
      }

      if (aImportsB || bImportsA || sharedSymbols) {
        union(i, j)
      }
    }
  }

  // Collect dependency groups
  const groups = new Map<number, Array<IFileConflictContext>>()
  for (let i = 0; i < files.length; i++) {
    const root = find(i)
    let group = groups.get(root)
    if (group === undefined) {
      group = []
      groups.set(root, group)
    }
    group.push(files[i])
  }

  // Pack groups into chunks: large groups get split, small groups bin-pack
  const result: Array<Array<IFileConflictContext>> = []
  let currentBin: Array<IFileConflictContext> = []

  for (const group of groups.values()) {
    if (group.length >= targetSize) {
      if (currentBin.length > 0) {
        result.push(currentBin)
        currentBin = []
      }
      for (let i = 0; i < group.length; i += targetSize) {
        result.push(group.slice(i, i + targetSize))
      }
    } else {
      if (currentBin.length + group.length > targetSize) {
        if (currentBin.length > 0) {
          result.push(currentBin)
        }
        currentBin = [...group]
      } else {
        currentBin.push(...group)
      }
    }
  }

  if (currentBin.length > 0) {
    result.push(currentBin)
  }

  return result
}
